import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    BulkSelectEmployeeDialogComponent,
    DeprecatedModalComponent,
    DeprecatedModalFormComponent,
    PdfViewerComponent,
} from '@app/components';
import { BenefitDivision } from '@app/models/benefits/benefit-division.model';
import { BenefitPlan as EmployeeBenefitPlan } from '@app/models/employee/benefits/benefit-plan.model';
import { Employee } from '@app/models/employee/employee.model';
import { BenefitShowMenuService } from '@app/modules/benefits/services/benefits-menu.service';
import { MenuColumnEvent } from '@app/modules/table/interfaces/menu-column-event.interface';
import { AuthService } from '@app/services/auth.service';
import { NotifyService } from '@app/services/notify.service';
import { BenefitsResolver } from '@benefits/benefits.resolver';
import { EmployeesIndexTable } from '@benefits/services/tables/employees-table/employees-index.table';
import { IndexView } from '@common/views/index/index.view';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';

@Component({
    templateUrl: './employee-index.template.html',
    styleUrls: ['./employee-index.style.scss'],
})
export class EmployeeIndexView extends IndexView implements OnInit {
    isLoading = false;
    benefitPlan: BenefitPlan;
    table: EmployeesIndexTable;
    @ViewChild('selectEmployees', { static: true }) selectEmployees: BulkSelectEmployeeDialogComponent;
    @ViewChild('switchDivisionModal', { static: true }) switchDivisionModal: DeprecatedModalFormComponent;
    @ViewChild('switchPlanModal', { static: true }) switchPlanModal: DeprecatedModalFormComponent;
    @ViewChild('pdfPreviewModal', { static: true }) pdfPreviewModal: DeprecatedModalComponent;
    @ViewChild('pdfViewer', { static: true }) pdfPreview: PdfViewerComponent;

    currentEmployee: Employee;
    currentEmployeeBenefitPlan: EmployeeBenefitPlan;

    newDivision: BenefitDivision = new BenefitDivision();
    newBenefitPlan: BenefitPlan = new BenefitPlan();

    allBenefitPlans: BenefitPlan[] = [];

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private notify: NotifyService,
        private auth: AuthService,
        private resolver: BenefitsResolver,
        public benefitShowMenuService: BenefitShowMenuService
    ) {
        super();

        this.benefitPlan = this.route.snapshot.data.models.get('benefitPlan');

        const employeeIds = this.benefitPlan.employees.map((employee) => employee.id);

        this.table = new EmployeesIndexTable(this.auth, employeeIds);

        this.table.onLoading.subscribe((isLoading) => (this.isLoading = isLoading));
        this.table.reload();
    }

    ngOnInit(): void {
        this.getAllBenefitPlans();
    }

    get isEmpty(): boolean {
        return this.table.paginator.empty && !this.table.activeFilters.size && !this.table.paginator.search;
    }

    onCreate(): void {}

    onSave(attach: number[], detach: number[]): void {
        this.isLoading = true;

        this.benefitPlan
            .attach('employees', attach)
            .detach('employees', detach)
            .param('company', this.auth.company.id)
            .save()
            .then(() => {
                this.resolve();
                this.notify.success('Benefit plan saved');
            })
            .catch((err) => this.notify.error(err.message || 'Unable to save benefits plan'));
    }

    resolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.benefitPlan = <BenefitPlan>models.get('benefitPlan');

            const employeeIds = this.benefitPlan.employees.map((employee) => employee.id);
            this.table = new EmployeesIndexTable(this.auth, employeeIds);

            this.isLoading = false;
        });
    }

    async onTableMenuEvent(event: MenuColumnEvent): Promise<void> {
        const eventType = event.menuItem.event;
        const employeeBenefitPlan: EmployeeBenefitPlan = event.data as EmployeeBenefitPlan;

        this.currentEmployee = employeeBenefitPlan.employee;

        const employeeBenefitPlanWithIncludes = await this.getEmployeeBenefitPlan(employeeBenefitPlan.id);
        this.currentEmployeeBenefitPlan = employeeBenefitPlanWithIncludes;

        switch (eventType) {
            case 'switchDivision':
                this.currentEmployeeBenefitPlan.divisionEffectiveAt = null;
                this.switchDivisionModal.show();
                break;

            case 'changePlan':
                this.currentEmployeeBenefitPlan.effectiveAt = null;
                this.switchPlanModal.show();
                break;
        }
    }

    onCancel(): void {}

    setNewDivision(selectedBenefitDivision: BenefitDivision): void {
        this.newDivision = selectedBenefitDivision;
    }

    setNewBenefitPlan(selectedBenefitPlan: BenefitPlan): void {
        this.newBenefitPlan = selectedBenefitPlan;
    }

    switchDivision(): void {
        this.isLoading = true;

        this.currentEmployeeBenefitPlan.benefitDivision = this.newDivision;

        this.currentEmployeeBenefitPlan
            .param('employee', this.currentEmployee.id)
            .save()
            .then(() => {
                this.table.reload();
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'Division could not be updated.');
            });
    }

    switchBenefitPlan(): void {
        this.isLoading = true;

        this.currentEmployeeBenefitPlan.benefitPlan = this.newBenefitPlan;
        this.currentEmployeeBenefitPlan
            .param('employee', this.currentEmployee.id)
            .save()
            .then(() => {
                this.table.reload();
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err.message || 'Plan cannot be updated.');
            });
    }

    private async getAllBenefitPlans(): Promise<void> {
        this.isLoading = true;

        const [benefitPlans] = await BenefitPlan.param('company', this.auth.company.id).all();
        this.allBenefitPlans = benefitPlans;

        this.isLoading = false;
    }

    private async getEmployeeBenefitPlan(employeeBenefitPlanId: number): Promise<EmployeeBenefitPlan> {
        return EmployeeBenefitPlan.param('employee', this.currentEmployee.id)
            .with(['benefitPlan', 'benefitDivision'])
            .find(employeeBenefitPlanId);
    }
}
